// MACHINERY V2 (Stroje) — the 48px PHONE header (M2.16a).
//
// Spec: mobile/README.md §Standards — "Phone chrome: 48px header (logo · avatar
// · hamburger). Tablet keeps the full desktop ChromeBar." Rendered truth:
// mobile/screenshots/01-filter-b-chosen.png. Reference impl:
// mobile/machinery-mobile.jsx MbChrome (:17-28).
//
// >>> IT REPLACES ChromeNavV4 BELOW 768 ONLY. <<< At 768-1279 the FULL
// ChromeBar stays — screenshot 07-v1-tablet-768.png draws it with all four
// module tabs and the whole right cluster, and a tablet has the width for it.
// The branch lives in MachineryV2Layout; this component never decides when it
// renders.
//
// EVERYTHING THE 52px BAR CARRIED THAT DOES NOT FIT IN 48px IS IN THE DRAWER,
// not deleted: module tabs, Midas, the bell, language, palette, settings,
// log-out. See MobileDrawer.tsx. The avatar here is deliberately NON-INTERACTIVE
// — the hamburger is the single opener, because two targets 8px apart that open
// the same panel is a mis-tap generator, and the drawer leads with the identity
// block the avatar would have opened.
//
// ============================================================
//  THE UNREAD COUNT RIDES THE HAMBURGER — A COUNT, NOT A DOT
// ============================================================
// ChromeNavV4.tsx:198-240 paints a red unread count on an ALWAYS-VISIBLE bell.
// The drawer paints the same count correctly (mobileDrawerRows.tsx DrawerBadge)
// — but only once it has been opened, and a user with 7 unread has no reason to
// open a menu that looks idle. Re-homing the bell without re-homing its SIGNAL
// deletes the signal. The number is the information, so it is the number that
// is shown, not a dot: "7 waiting" and "1 waiting" are different decisions.
//
// SAME SELECTOR, SAME GATE, NO SECOND FETCH: the read is ChromeNavV4's
// (`s.notifications?.unreadCount`) and the gate is `canSeeModule(Notifications)`
// — the same one the bell and the drawer row use, because /notifications sits
// behind PermissionRouteGuard(Notifications). It stays READ-ONLY for the same
// reason the bell is: the legacy bell still owns fetchUnreadCount and the
// SignalR subscription, and M2.12's right-cluster audit must re-home those or
// this badge silently reads 0 forever.
//
// The badge is aria-hidden and the COUNT is carried by the button's own
// aria-label instead — the pattern filterBarAtoms.tsx:172-176 + :197-200 already
// uses for its Filtre badge, so a screen reader hears one sentence, not a label
// followed by a bare numeral. (It cited FilterBarB.tsx until M2.16c moved both
// controls into filterBarAtoms.tsx; the pointer was left behind.)
//
// The wordmark is non-interactive for the same reason it is in ChromeNavV4:
// v2 is still a parallel surface, and M2.12 may wire it to the default route.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { AccountState } from "../../../../account/models/AccountModels";
import { Modules } from "../../../../core/models/PermissionModels";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { MbMenuGlyph } from "../mobile/mobileGlyphs";
import { DrawerBadge } from "./mobileDrawerRows";
import { ChromeNavModuleKey, NAV_MODULES, canSeeModule } from "./navModules";

export interface MobilePhoneHeaderProps {
  onOpenMenu: () => void;
  active?: ChromeNavModuleKey;
}

const MobilePhoneHeader: React.FC<MobilePhoneHeaderProps> = ({
  onOpenMenu,
  active = "stroje",
}) => {
  const { translate } = useTranslation(["myMachinery", "layout"]);
  const { user } = useAppSelector<AccountState>((s: any) => s.account);
  const unreadCount = useAppSelector((s: any) => s.notifications?.unreadCount ?? 0);
  const accent = NAV_MODULES.find((m) => m.key === active)?.accent || "var(--mod-accent)";
  const showUnread = canSeeModule(Modules.Notifications) && unreadCount > 0;

  return (
    <div
      style={{
        height: 48,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 14px",
        background: "var(--card)",
        borderBottom: "1px solid var(--border)",
        position: "relative",
        zIndex: 5,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: "var(--ink)",
          whiteSpace: "nowrap",
        }}
      >
        MACHINE
        <span
          style={{
            background: "var(--ink)",
            color: "var(--bg)",
            borderRadius: 3,
            padding: "1px 3px",
            marginLeft: 2,
          }}
        >
          ID
        </span>
      </span>

      <span style={{ flex: 1 }} />

      {user && (
        <span
          aria-hidden
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: accent,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {user.firstName?.charAt(0)?.toUpperCase() || "?"}
        </span>
      )}

      <button
        type="button"
        className="btn btn-ghost"
        onClick={onOpenMenu}
        aria-haspopup="dialog"
        aria-label={
          showUnread
            ? translate("myMachinery:v2.mobile.menuUnread", { count: unreadCount })
            : translate("myMachinery:v2.mobile.menu")
        }
        style={{
          width: 44,
          height: 44,
          padding: 0,
          justifyContent: "center",
          color: "var(--ink-2)",
          flexShrink: 0,
          // Anchors the unread badge below; the glyph stays centred.
          position: "relative",
          // The 48px bar is 14px-padded; a 44px target inside it would push the
          // hamburger 5px off the right margin, so the box overhangs into the
          // padding instead of moving the glyph.
          marginRight: -9,
        }}
      >
        <MbMenuGlyph />
        {showUnread && (
          // Pinned to the glyph's top-right corner INSIDE the 44px box, so the
          // target keeps both its axes and the badge never leaves the header.
          // The drawer's own badge drawing, not a third one — and not
          // ChromeNavV4's 14px bell badge, which is tuned to a 16px glyph in a
          // 52px desktop bar.
          <span style={{ position: "absolute", top: 3, right: 7, display: "flex" }}>
            <DrawerBadge count={unreadCount} />
          </span>
        )}
      </button>
    </div>
  );
};

export default MobilePhoneHeader;
